import { Controller } from "@hotwired/stimulus"
import { Editor } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import Youtube from "@tiptap/extension-youtube"
import { TableKit } from "@tiptap/extension-table"
import { Placeholder } from "@tiptap/extensions"
import { SlashCommands } from "../editor/slash_commands"
import { Shortcuts } from "../editor/shortcuts"
import { FloatingToolbar } from "../editor/floating_toolbar"
import { LinkPopover } from "../editor/link_popover"
import { YouTubeBlock } from "../editor/youtube_block"

// A rich text editor over two hidden fields.
//
//   <div data-controller="rich-text-editor"
//        data-rich-text-editor-placeholder-value="Write…">
//     <input type="hidden" name="doc[body]"    data-rich-text-editor-target="html">
//     <input type="hidden" name="doc[content]" data-rich-text-editor-target="json">
//     <div class="editor" data-rich-text-editor-target="editor"></div>
//   </div>
//
// The fields are the form's truth. On every change the controller writes the
// editor's HTML to one and its JSON to the other, so the server receives both
// and the page can be reloaded into the editor exactly as it was left. With no
// JavaScript the fields still submit whatever was loaded into them.
//
// The editor opens from the JSON when there is any, otherwise from the HTML —
// which is what a document written through MCP has — so either column is enough
// to start from. TipTap (ProseMirror) is the one npm dependency here; the menus
// and toolbar around it are plain DOM in ../editor/, not a framework.
//
// Exposed for other scripts: `value`, `getHTML()`, `getJSON()`, `setContent()`,
// and a `rich-text-editor:change` event with both formats in `detail`.
export default class extends Controller {
  static targets = ["editor", "html", "json"]
  static values = {
    placeholder: { type: String, default: "Write, or type / for a block…" }
  }

  connect() {
    // Idempotent: Turbo can restore a page with the editor already drawn.
    this.teardown()

    this.linkPopover = null
    this.editor = new Editor({
      element: this.editorTarget,
      content: this.initialContent(),
      editorProps: {
        attributes: { class: "editor__content", role: "textbox", "aria-multiline": "true" }
      },
      extensions: [
        StarterKit.configure({
          heading: { levels: [1, 2, 3] },
          link: { openOnClick: false, autolink: true, defaultProtocol: "https" }
        }),
        Placeholder.configure({ placeholder: this.placeholderValue }),
        Youtube.configure({ nocookie: true, width: 640, height: 360 }),
        TableKit.configure({ table: { resizable: false } }),
        Shortcuts.configure({ onLink: () => this.linkPopover?.open() }),
        SlashCommands.configure({
          onYoutube: () => this.youtubeBlock.insert()
        })
      ],
      onUpdate: () => this.sync(),
      onCreate: () => this.sync()
    })

    this.linkPopover = new LinkPopover(this.editor)
    this.youtubeBlock = new YouTubeBlock(this.editor)
    this.toolbar = new FloatingToolbar(this.editor, { onLink: () => this.linkPopover.open() })
  }

  disconnect() {
    this.teardown()
  }

  teardown() {
    this.toolbar?.destroy()
    this.linkPopover?.destroy()
    this.youtubeBlock?.destroy()
    this.editor?.destroy()
    this.toolbar = this.linkPopover = this.youtubeBlock = this.editor = null
    this.editorTarget.replaceChildren()
  }

  // --- Content -------------------------------------------------------------

  initialContent() {
    const json = this.jsonTarget.value.trim()
    if (json) {
      try {
        return JSON.parse(json)
      } catch {
        // A hand-edited or truncated value: fall through to the HTML.
      }
    }
    return this.htmlTarget.value || ""
  }

  sync() {
    if (!this.editor) return
    const html = this.editor.isEmpty ? "" : this.editor.getHTML()
    const json = JSON.stringify(this.editor.getJSON())
    this.htmlTarget.value = html
    this.jsonTarget.value = json
    this.dispatch("change", { detail: { html, json: this.editor.getJSON() } })
  }

  get value() {
    return this.getJSON()
  }

  getHTML() {
    return this.editor?.getHTML() ?? ""
  }

  getJSON() {
    return this.editor?.getJSON() ?? null
  }

  // Accepts editor JSON or an HTML string.
  setContent(content) {
    this.editor?.commands.setContent(content)
    this.sync()
  }
}
